import { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import type { RoleResponse } from '../../types/role';

interface RoleDialogProps {
  /** Existing custom role to edit; omit to create a new one. Mount a fresh instance per open (use `key`). */
  initial?: RoleResponse | null;
  loading: boolean;
  onClose: () => void;
  onSave: (name: string, description: string) => void;
}

export function RoleDialog({ initial, loading, onClose, onSave }: RoleDialogProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [desc, setDesc] = useState(initial?.description ?? '');
  const [touched, setTouched] = useState(false);

  const nameError = touched && !name.trim() ? 'Enter a name so people can recognise this role' : '';

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!name.trim()) return;
    onSave(name.trim(), desc.trim());
  };

  return (
    <Dialog open onClose={loading ? undefined : onClose} fullWidth maxWidth="xs">
      <form onSubmit={submit} noValidate>
        <DialogTitle sx={{ fontWeight: 700 }}>{initial ? 'Edit role details' : 'New role'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField
            label="Role name"
            required
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setTouched(true)}
            error={!!nameError}
            helperText={nameError || 'Shown wherever this role is assigned to a user.'}
            fullWidth
            size="small"
          />
          <TextField
            label="Description"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            helperText="Optional. Say who this role is for, e.g. “Front-office staff”."
            fullWidth
            size="small"
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button color="inherit" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Saving…' : initial ? 'Save changes' : 'Create role'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
